import {
  Container,
  Form,
  OptionValue,
  ReactSelect,
  Select,
  SelectMultiple,
} from '@valid-eval/shared-react-components';
import React, { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import GenericModal from 'components/GenericModal';
import { load } from 'data/actions/roleSummaries';
import {
  getResultsForSelectedPhase,
  getSelectedPhaseIdForCurrentEvent,
  getTeamsAsOptions,
} from 'data/reducers';
import withRouter from 'routes/withRouter';
import StatusAndRangeSelector from 'screens/app/results/StatusAndRangeSelector';
import { isTeamLead } from 'utils';

import { filterData } from './TeamSelector';

export type PDFTeamStatusCheckboxes = {
  in: boolean;
  out: boolean;
  bubble: boolean;
  empty: boolean;
};

type OwnProps = {
  eventId: string;
  show: boolean;
  title?: string;
  onCancel(): void;
  onConfirm({
    teams,
    isSinglePDF,
    showBoxPlot,
    selectedPdfReportArtifact,
    pdfTeamStatusCheckboxes,
  }: {
    teams: OptionValue[];
    isSinglePDF: boolean;
    showBoxPlot?: boolean;
    selectedPdfReportArtifact: OptionValue | null;
    pdfTeamStatusCheckboxes: PDFTeamStatusCheckboxes;
  }): void;
  phaseFinalRank: boolean;
  pdfTeamRange: { first: number | string; last: number | string };
  totalEvaluations: number;
  pdfTeamStatusCheckboxes: PDFTeamStatusCheckboxes;
  handleOnChangePDFTeamStatusCheckbox: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setPdfTeamRange: (range: { first: number | string; last: number | string }) => void;
  isSinglePDF: boolean;
  setIsSinglePDF?: (value: boolean) => void;
  showBoxPlot?: boolean;
  setShowBoxPlot?: (value: boolean) => void;
  isQualitativeMode?: boolean;
  pdfReportArtifacts?: OptionValue[];
  isForTeams?: boolean;
};

const connector = connect(
  (state, ownProps: OwnProps) => ({
    teamOptions: getTeamsAsOptions(state, ownProps),
    // @ts-ignore
    selectedPhaseId: getSelectedPhaseIdForCurrentEvent(state, ownProps),
    // @ts-ignore
    evaluations: getResultsForSelectedPhase(state, ownProps),
  }),
  {
    load,
  },
);

type PropsFromRedux = ConnectedProps<typeof connector>;
type TeamSelectorModalProps = PropsFromRedux & OwnProps;

const TeamSelectorModal = ({
  load,
  show,
  onCancel,
  onConfirm,
  selectedPhaseId,
  title,
  phaseFinalRank,
  pdfTeamRange,
  totalEvaluations,
  pdfTeamStatusCheckboxes,
  evaluations,
  handleOnChangePDFTeamStatusCheckbox,
  setPdfTeamRange,
  isSinglePDF,
  setIsSinglePDF,
  showBoxPlot,
  setShowBoxPlot,
  isQualitativeMode,
  pdfReportArtifacts,
  isForTeams,
}: TeamSelectorModalProps) => {
  const filteredTeams = filterData(evaluations, pdfTeamStatusCheckboxes);
  const [selectedTeams, setSelectedTeams] = useState<OptionValue[]>(filteredTeams);
  const [selectedPdfReportArtifact, setSelectedPdfReportArtifact] = useState<OptionValue | null>(
    pdfReportArtifacts?.[0] ?? null,
  );
  const showBoxPlotCheckbox = !(isQualitativeMode && isTeamLead()) && !!setShowBoxPlot;
  const showSinglePDFCheckbox = !!setIsSinglePDF;

  useEffect(() => {
    load('teams', selectedPhaseId);
  }, [selectedPhaseId, load]);

  useEffect(() => {
    setSelectedTeams(filteredTeams);
  }, [pdfTeamStatusCheckboxes, evaluations]);

  const handleTeamsChange = (
    value: ReactSelect.MultiValue<OptionValue> | ReactSelect.SingleValue<OptionValue>,
  ) => {
    const newSelectedTeams = value as OptionValue[];
    setSelectedTeams(newSelectedTeams);
  };

  const handleConfirm = () =>
    onConfirm({
      teams: selectedTeams,
      isSinglePDF,
      showBoxPlot,
      selectedPdfReportArtifact,
      pdfTeamStatusCheckboxes,
    });

  return (
    <GenericModal
      danger={isForTeams}
      isButtonDisabled={selectedTeams.length === 0}
      cancelButton="Cancel"
      confirmButton="Export PDF"
      body={
        <Container>
          {pdfReportArtifacts?.length && (
            <>
              <Form.Label htmlFor="pdf-report-artifact-select">
                Choose a PDF report artifact
              </Form.Label>
              <Form.Group>
                <Form.Control
                  className="p-0"
                  id="pdf-report-artifact-select"
                  as={Select}
                  // @ts-expect-error Select expects a SelectOption type for defaultValue
                  defaultValue={pdfReportArtifacts[0]}
                  options={pdfReportArtifacts}
                  // @ts-expect-error onChange will call setSelectedPdfReportArtifact with a SelectOption
                  onChange={setSelectedPdfReportArtifact}
                  isClearable={false}
                  isSearchable={false}
                  isForm
                />
              </Form.Group>
            </>
          )}
          <p className="mt-4">
            Choose the status of the teams you would like to include in the PDF.
          </p>
          <StatusAndRangeSelector
            phaseFinalRank={phaseFinalRank}
            pdfTeamRange={pdfTeamRange}
            totalEvaluations={totalEvaluations}
            pdfTeamStatusCheckboxes={pdfTeamStatusCheckboxes}
            handleOnChangePDFTeamStatusCheckbox={handleOnChangePDFTeamStatusCheckbox}
            setPdfTeamRange={setPdfTeamRange}
          />
          {!phaseFinalRank && (
            <>
              <p className="mt-4">Choose the teams you would like to include in the PDF.</p>
              <SelectMultiple
                placeholder="Filter by name, prefix or panel"
                options={filteredTeams}
                value={selectedTeams}
                onChange={handleTeamsChange}
                backspaceRemovesValue={false}
              />
            </>
          )}
          {(showSinglePDFCheckbox || showBoxPlotCheckbox) && (
            <div className="d-flex align-items-center">
              {showSinglePDFCheckbox && (
                <span className="form-check me-4 mt-4">
                  <Form.Check
                    id={'single-PDF-checkbox-1'}
                    type={'checkbox'}
                    onChange={(e) => setIsSinglePDF(e.target.checked)}
                    checked={isSinglePDF}
                    label={'Download all reports in a single PDF'}
                  />
                </span>
              )}
              {showBoxPlotCheckbox && (
                <span className="form-check mt-4">
                  <Form.Check
                    id={'set-show-plotbox-checkbox'}
                    type={'checkbox'}
                    onChange={(e) => setShowBoxPlot(e.target.checked)}
                    checked={showBoxPlot}
                    label={'Display Box Plots'}
                  />
                </span>
              )}
            </div>
          )}
          {isForTeams && (
            <div className="alert alert-warning text-justify mt-3">
              <b className="text-red01">STOP!!</b> Exporting the "for Team" PDF will disable all
              ability to modify or add additional comments for selected teams. Only selection
              committee comments will be available to add once these PDFs are generated.
            </div>
          )}
        </Container>
      }
      name={'team-files-report-confirm'}
      title={title}
      onCancel={onCancel}
      onConfirm={handleConfirm}
      show={show}
    />
  );
};

export default withRouter(connector(TeamSelectorModal));
